import { useEffect, useRef } from "react";

import { typeOptions } from "@/pages/root/shop/FilterAndSortListings";
import type { ListingTypeOptions } from "@/utils/productTypes";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right_small.svg";
import { ImagePreloader } from "atoms/image-preloader/ImagePreloader";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { JustForYouTag } from "atoms/tag/JustForYouTag";
import { ProductBenefitTag } from "atoms/tag/ProductBenefitTag";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { getPricingText } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";

interface ProductCardLargeProps {
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  type: ListingTypeOptions;
  tag?: string | null;
  concerns?: Array<string> | null;
  price: number;
  memberPrice?: number | null;
  onClick?: () => void;
  unitType?: string | null | undefined;
  hasLinearPricing?: boolean | undefined;
  discount?: string | null | undefined;
  customColor?: string | null;
  guestMode?: boolean;
  priceRange?: string | null;
  isRecommended?: boolean;
  /** Optional: ID of the item to enable prefetching */
  itemId?: string;
  bookingEnabled?: boolean;
}

export const ProductCardLarge = (props: ProductCardLargeProps) => {
  const isV3 = true;

  if (isV3) {
    return <ProductCardLargeV3 {...props} />;
  }

  return <ProductCardLargeV2 {...props} />;
};

// V3 Design - New luxury card design
const ProductCardLargeV3 = ({
  title,
  description,
  imageUrl,
  type,
  tag,
  price,
  memberPrice,
  unitType,
  hasLinearPricing = false,
  discount,
  customColor,
  guestMode,
  priceRange,
  onClick,
  isRecommended = false,
  itemId,
  bookingEnabled = false,
}: ProductCardLargeProps) => {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchPackagePDP, prefetchMembershipPDP, prefetchProductPDP } =
    usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting || !itemId) return;

    switch (type) {
      case "Treatment":
      case "Package":
        prefetchPackagePDP(itemId);
        break;
      case "Membership":
        prefetchMembershipPDP(itemId);
        break;
      case "Product":
      case "ProductFromExternalSource":
        prefetchProductPDP(itemId);
        break;
    }
  }, [
    isIntersecting,
    itemId,
    type,
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
  ]);

  const shouldShowBookNow = bookingEnabled && price == 0;

  return (
    <div
      ref={ref}
      className="group relative flex min-h-[140px] cursor-pointer flex-col overflow-hidden rounded-lg bg-white shadow-subtle backdrop-blur-sm transition-shadow active:scale-[0.98]"
      onClick={onClick}
    >
      <div className="flex flex-1">
        {/* Image Section - Left Side with Fade */}
        <div className="relative w-[120px] flex-shrink-0">
          {imageUrl ? (
            <ImagePreloader
              url={imageUrl}
              lazy={true}
              fade={150}
              preloadDistance="100px"
              className="h-full w-full"
              placeholder={
                <div className="h-full w-full animate-pulse bg-gray-100" />
              }
              errorPlaceholder={
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-2">
                  <span className="text-center text-[10px] leading-tight text-gray-400">
                    IMAGE COMING SOON
                  </span>
                </div>
              }
            >
              <img
                className="h-full w-full object-cover"
                src={imageUrl}
                alt={title}
              />
            </ImagePreloader>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-2">
              <span className="text-center text-[10px] leading-tight text-gray-400">
                IMAGE COMING SOON
              </span>
            </div>
          )}
          {/* Transparent gradient fade from image to card */}
          <div className="absolute right-0 top-0 h-full w-4 bg-gradient-to-r from-transparent to-white/80" />
        </div>

        {/* Content Section - Right Side */}
        <div className="flex flex-1 flex-col px-2.5 pb-2 pt-2">
          {/* Tags Row - All tags on same line with wrapping */}
          {(isRecommended || discount || tag) && (
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {isRecommended && <JustForYouTag />}
              {discount && (
                <DescriptiveTag
                  color={customColor ? "custom" : "primary"}
                  size="small"
                  customColor={customColor}
                >
                  {discount}
                </DescriptiveTag>
              )}
              {tag && (
                <DescriptiveTag color="primary" size="small">
                  {tag}
                </DescriptiveTag>
              )}
            </div>
          )}

          {/* Title - At the top */}
          <h3 className="mb-1 line-clamp-2 font-serif text-h3 leading-snug tracking-tight text-primary">
            {title}
          </h3>

          {/* Description - Truncated */}
          {description && (
            <p className="line-clamp-3 break-words pb-1 pr-1 text-body2 leading-snug text-secondary">
              {description}
            </p>
          )}

          {/* Spacer to push price to bottom */}
          <div className="flex-grow" />
        </div>
      </div>

      {/* Price Section - Full Width Bottom */}
      <div className="flex items-center justify-between bg-one px-3 py-1.5">
        <div className="flex items-center gap-2">
          {guestMode && (
            <span className="text-body2 text-primary">
              Sign up to see price
            </span>
          )}
          {!guestMode &&
            (shouldShowBookNow ? (
              <span className="text-body2 font-semibold text-primary">
                Book Now
              </span>
            ) : (
              <>
                <span
                  className={`text-body2 text-primary ${
                    memberPrice != null && memberPrice !== price && !priceRange
                      ? ""
                      : "font-semibold"
                  }`}
                >
                  {priceRange ||
                    getPricingText(hasLinearPricing, price, unitType, type)}
                </span>
                {memberPrice != null &&
                  memberPrice !== price &&
                  !priceRange && (
                    <>
                      <span className="text-body2 text-neutral-300">|</span>
                      <span className="text-body2 font-semibold text-primary">
                        {formatCurrency(memberPrice)}{" "}
                        <span className="font-normal">Member</span>
                      </span>
                    </>
                  )}
              </>
            ))}
        </div>
        <Icon svg={ChevronRight} size="xsmall" color="brandColor" />
      </div>
    </div>
  );
};

// V2 Design - Original card design
const ProductCardLargeV2 = ({
  title,
  description,
  imageUrl,
  type,
  tag,
  concerns,
  price,
  memberPrice,
  unitType,
  hasLinearPricing = false,
  discount,
  customColor,
  guestMode,
  priceRange,
  onClick,
  isRecommended = false,
  itemId,
}: ProductCardLargeProps) => {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const { prefetchPackagePDP, prefetchMembershipPDP, prefetchProductPDP } =
    usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting || !itemId) return;

    switch (type) {
      case "Treatment":
      case "Package":
        prefetchPackagePDP(itemId);
        break;
      case "Membership":
        prefetchMembershipPDP(itemId);
        break;
      case "Product":
      case "ProductFromExternalSource":
        prefetchProductPDP(itemId);
        break;
    }
  }, [
    isIntersecting,
    itemId,
    type,
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
  ]);

  const tags: React.MutableRefObject<HTMLDivElement[]> = useRef([]);
  return (
    <div
      ref={ref}
      className="cursor-pointer flex-col rounded-lg bg-white pt-2 shadow-subtle"
      onClick={onClick}
    >
      <div className="flex justify-between pb-3 pl-3 pr-2">
        <div className="flex min-w-0 flex-col pt-1">
          <div className="flex gap-1 pb-3">
            {isRecommended && <JustForYouTag />}
            {discount && (
              <DescriptiveTag
                color={customColor ? "custom" : "primary"}
                size="small"
                customColor={customColor}
              >
                {discount}
              </DescriptiveTag>
            )}
            <DescriptiveTag color="secondary" size="small">
              {typeOptions.get(type) || type}
            </DescriptiveTag>
            {tag && (
              <DescriptiveTag color="primary" size="small">
                {tag}
              </DescriptiveTag>
            )}
          </div>
          <div className="line-clamp-2 break-words pr-2 text-left font-serif text-h3 text-primary">
            {title}
          </div>
        </div>
        {imageUrl && (
          <ImagePreloader
            url={imageUrl}
            lazy={true}
            fade={150}
            preloadDistance="100px"
            className="h-[81px] w-[108px]"
            placeholder={
              <div className="h-[81px] w-[108px] animate-pulse rounded bg-gray-100" />
            }
            errorPlaceholder={
              <div className="flex h-[81px] w-[108px] items-center justify-center rounded bg-gray-200">
                <span className="text-xs text-gray-500">No image</span>
              </div>
            }
          >
            <img
              className="h-[81px] w-[108px] max-w-none rounded object-cover"
              src={imageUrl}
              height={100}
              alt={title}
            />
          </ImagePreloader>
        )}
      </div>
      {description && (
        <div className="mb-3 line-clamp-3 pl-3 pr-2 text-left text-body2 text-secondary">
          {description}
        </div>
      )}
      {concerns && concerns.length > 0 && (
        <div className="flex flex-wrap justify-start gap-1 overflow-hidden pb-3 pl-3 pr-2">
          {concerns?.map((concern) => (
            <div
              className="flex flex-col gap-2"
              key={concern}
              ref={(tag) => {
                if (tag != null) {
                  tags.current.push(tag);
                }
              }}
            >
              <ProductBenefitTag>{concern}</ProductBenefitTag>
            </div>
          ))}
        </div>
      )}
      <div className="mt-1 flex w-full items-center justify-between rounded-b-lg bg-one py-3 pl-3 pr-2">
        <div className="flex">
          {guestMode && (
            <span className="text-body2">Sign up to see price</span>
          )}
          {!guestMode && (
            <>
              <span className="flex text-body2 text-secondary">
                {priceRange ||
                  getPricingText(hasLinearPricing, price, unitType, type)}
              </span>
              {memberPrice != null && memberPrice !== price && !priceRange && (
                <>
                  <span className="-mt-[1px] pl-1.5 text-[17px] text-body2 text-primary">
                    {" "}
                    |{" "}
                  </span>
                  <span className="pl-1.5 text-body2 text-primary">
                    {formatCurrency(memberPrice)} Member
                  </span>
                </>
              )}
            </>
          )}
        </div>
        <Icon svg={ChevronRight} size="xsmall" color="brandColor" />
      </div>
    </div>
  );
};
